import { Color } from '@/constants/color';
import { AppointmentAttendanceDrugScreen } from '@/types/appointments';
import { DrugScreenResult } from '@/types/drugScreen';

interface BaseDrugScreenMeta<Tag extends string> {
  readonly _tag: Tag;
  color: Color;
  emoji: string;
}
type DrugScreenMissedOrAbandoned = BaseDrugScreenMeta<'_missedOrAbandoned'>;
type DrugScreenData = BaseDrugScreenMeta<'_result'>;
type DrugScreenUnknown = BaseDrugScreenMeta<'_unknown'>;

export type DrugScreenMetaType = DrugScreenMissedOrAbandoned | DrugScreenData | DrugScreenUnknown;

/**
 * This object represents the concept of a enum with associated values
 * This object can be improved with new props and also provide a way to discriminate cases
 * and create a pattern matching function to simulate a switch case with callbacks for each case
 * for example:
 * @example
 * ```
 * match(result)({
 *    MissedOrAbandoned: (data) => {...},
 *    Result: (data) => {...},
 *    Unknown: (data) => {...},
 * })
 * ```
 * */
export const DrugScreenMeta = {
  MissedOrAbandoned: (): DrugScreenMissedOrAbandoned => ({
    _tag: '_missedOrAbandoned',
    color: Color.ORANGE,
    emoji: '❌'
  }),
  Result: (isCompliant: boolean): DrugScreenData => ({
    _tag: '_result',
    color: isCompliant ? Color.GREEN : Color.RED,
    emoji: isCompliant ? '✅' : '❌'
  }),
  Unknown: (): DrugScreenUnknown => ({
    _tag: '_unknown',
    color: Color.YELLOW,
    emoji: '❓'
  })
};

const knownResults = [
  DrugScreenResult.FORMAL_NEGATIVE,
  DrugScreenResult.NEGATIVE,
  DrugScreenResult.POSITIVE,
  DrugScreenResult.FORMAL_POSITIVE
];
const hasKnownResult = (result: DrugScreenResult) => knownResults.includes(result);

export const getDrugScreenMeta = (
  drugScreen: AppointmentAttendanceDrugScreen
): DrugScreenMetaType => {
  if (
    drugScreen.result === DrugScreenResult.ABANDONED ||
    drugScreen.result === DrugScreenResult.MISSED
  ) {
    return DrugScreenMeta.MissedOrAbandoned();
  }
  if (hasKnownResult(drugScreen.result) && Object.hasOwn(drugScreen, 'is_compliant')) {
    return DrugScreenMeta.Result(drugScreen.is_compliant);
  }

  return DrugScreenMeta.Unknown();
};
